import * as React from 'react';
import { Field, Form } from 'formik';
import * as Yup from 'yup';
import { Decimal } from 'decimal.js';
import {currency} from "~/lib/formatters";
import FormikForm from '~/lib/forms/FormikForm';
import SaleHeader from './SaleHeader';
import Select from '~/common/controls/Select';
import FooterWrapper from '~/common/elements/mobile/FooterWrapper';
import BodyWrapper from '~/common/elements/mobile/BodyWrapper';
import Button from '~/common/elements/mobile/Button';
import { buttonColor, buttonSize, buttonStyle } from '~/common/elements/mobile/consts';
import { navigate } from '~/router';
import { Reg } from '~/apps/pos/mobile/containers/sales/CreateSale';
import PageMobileWrapper from '~/common/elements/mobile/PageMobileWrapper';
import {
  groupingProductForSaleForm,
  parseFormData,
  productMaximumAmounts,
  productPrices,
  productType,
} from '~/apps/pos/shared/services/utils';
import Input from '~/common/controls/Input';
import MobileFormFieldWrapper from '~/common/controls/MobileFormFieldWrapper';
import decimal from '~/lib/normalize/decimal';
import Loader from '~/common/components/Loader';
import { UserShift } from '~/apps/pos/consts';
import { SearchableModelHelper } from "~/lib/forms/select";
import ModelSelect from "~/common/controls/ModelSelect";


const normalizeOptions = (productsList) => {
  if (!Array.isArray(productsList)) return [];

  return productsList.map((i) => {
    return {
      label: i.name,
      value: i.id,
    }
  })
};

type Props = {
  products: any,
  organization: any,
  orgLocationsHelper: SearchableModelHelper;
  userLocation: any,
  initialValues: Reg,
  onBack: any,
  onSubmit: any,
  onHelp: any,
  onClose: any,
  onChangeLocation: any,
  setFormData: any,
  formData: any,
  actionKeyBack: number,
  isLoad: boolean,
  hasDepartmentFeature: boolean,
  hasFHIFeature: boolean,
  userDepartment: any,
  userShift: string,
  departments: any,
  hasLocationAdministrativeFeeFeature: boolean,
  hasConvenienceFeeTextInSaleFeature: boolean,
  hasApplyMaximumDollarAmountsFeature: boolean,
}

const SaleStepProducts = (props: Props) => {
  const {
    initialValues, onBack, onHelp, onClose, onSubmit, setFormData, actionKeyBack, isLoad,
    organization, userLocation, products, orgLocationsHelper, onChangeLocation,
    hasDepartmentFeature, hasFHIFeature, userDepartment, userShift, departments, hasLocationAdministrativeFeeFeature,
    hasConvenienceFeeTextInSaleFeature, hasApplyMaximumDollarAmountsFeature,
  } = props;
  let currentTotal = new Decimal(0);
  const groupsProduct = groupingProductForSaleForm(products.ui.currentList.map(i => products.entities[i]));

  const createSchema = () => {
    const schemaDecimal = decimal().typeError('Must be an decimal');
    let valid: {[name: string]: any} = {
      locationId: Yup.string().typeError('Must select a location').required('Required'),
    };
    if (hasDepartmentFeature) {
      valid.departmentId = Yup.string().typeError('Must select a department').required('Required');
    }
    groupsProduct.forEach((i) => {
      if (i.productKey.search('custom_') != -1) {
        if (hasApplyMaximumDollarAmountsFeature && Decimal.isDecimal(i.maximumAmount)) {
          valid[i.productKey] = schemaDecimal.max(i.maximumAmount.add(0.01), `Amount must be less or equal $${currency(i.maximumAmount)}`);
        } else {
          valid[i.productKey] = schemaDecimal;
        }
      }
      if (i.productKey.search('item-not-dropdown_') != -1) {
        valid[i.productKey] = Yup.number().integer('Must be an integer').typeError('Must be an integer');
      }
    });
    return Yup.object().shape(valid);
  };
  const saleSchema = createSchema();

  const handleOnSubmit = (data) => {
    if (setFormData) setFormData(data, { total: currentTotal });
    return onSubmit(saleSchema.cast(data));
  };

  const handleHelp = onHelp ? () => { onHelp(actionKeyBack) } : null;

  return (
    <FormikForm
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={saleSchema}
      render={(propsForm) => {
        const { values } = propsForm;
        const prices = productPrices(products.entities);
        const maximumAmounts = productMaximumAmounts(products.entities);
        const selectedLocation = values.locationId && orgLocationsHelper.getSelectValue(values.locationId)?.entity;
        const administrativeFee = selectedLocation && selectedLocation.administrativeFee ? selectedLocation.administrativeFee
          : organization && organization.administrativeFee;
        const administrativeFeeMode = hasLocationAdministrativeFeeFeature && selectedLocation && selectedLocation.administrativeFeeMode ? selectedLocation.administrativeFeeMode
          : organization && organization.administrativeFeeMode;
        const parseData = parseFormData(values, prices, administrativeFee, administrativeFeeMode, hasApplyMaximumDollarAmountsFeature, maximumAmounts);
        const isTotal = parseData.total.comparedTo(0) > 0;
        currentTotal = parseData.grandTotal;
        const locationValue = userLocation ? userLocation.id : values.locationId;

        const getDepartmentOptions = () => {
          return departments.filter(dep => dep.locationId === locationValue).map(dep => {
            return {
              label: dep.name,
              value: dep.id,
            }
          })
        }

        const handleChangeLocation = (field, option) => {
          if (onChangeLocation) onChangeLocation(option.value);
        };

        if (isLoad) {
          return <Loader className="load--ol" />
        }

        return (
          <PageMobileWrapper className={isTotal ? "m__cont--withtotal" : "m__cont--withtotal --totalhidden" }>
            <SaleHeader
              onBack={onBack}
              onHelp={handleHelp}
              onClose={onClose}
              title="Product Selection"
            />
            <BodyWrapper>

              <div className="m__body__titl">{!organization ? '?' : organization.name}</div>

              <p className="m__body__info">
                <span className="m__body__info__icon"><span className="i--pin" /></span>
                <span className="m__body__info__note">{!userLocation ? '-' : userLocation.name}</span> <br/>
              </p>

              <Form className="form__cont">
                {
                  !userLocation && <MobileFormFieldWrapper
                    label={'Location'}
                    noPriceView
                  >
                    <Field
                      name="locationId"
                      placeholder="Select a location"
                      component={ModelSelect}
                      selectHelper={orgLocationsHelper}
                      className="input input--sm input--withdrop"
                      cbChange={handleChangeLocation}
                    />
                  </MobileFormFieldWrapper>
                }

                {
                  hasDepartmentFeature && !userDepartment && locationValue && <MobileFormFieldWrapper
                    label={'Department'}
                    noPriceView
                  >
                    <Field
                      name="departmentId"
                      placeholder="Select a department"
                      component={Select}
                      options={getDepartmentOptions()}
                      className="input input--sm input--withdrop"
                      isSearchable={false}
                    />
                  </MobileFormFieldWrapper>
                }

                {hasFHIFeature && !userShift && locationValue && (
                  <MobileFormFieldWrapper label={"Shift"} noPriceView>
                    <Field
                      name="shift"
                      placeholder="Select a shift"
                      component={Select}
                      options={UserShift.selectOptions()}
                      className="input input--sm input--withdrop"
                      isSearchable={false}
                    />
                  </MobileFormFieldWrapper>
                )}

                {
                  !!values.locationId && groupsProduct.map((i) => {
                    const xPrice = !parseData.resProducts[i.productKey] ? 0 : parseData.resProducts[i.productKey].price;

                    if (
                      i.productType === 'GROUP'
                      || i.productType === productType.SERVICE
                      || i.productType === productType.ITEM
                    ) {
                      return (
                        <MobileFormFieldWrapper
                          key={i.productKey}
                          label={i.productName}
                          price={xPrice}
                        >
                          <Field
                            name={i.productKey}
                            placeholder={`Select a ${i.productName}`}
                            component={Select}
                            options={normalizeOptions(i.productValues)}
                            className="input input--sm input--withdrop"
                            isSearchable={false}
                          />
                        </MobileFormFieldWrapper>
                      )
                    } else {
                      return (
                        <MobileFormFieldWrapper
                          key={i.productKey}
                          label={i.productName}
                          price={xPrice}
                          noPriceView={false}
                        >
                          <Field
                            key={i.productKey}
                            name={i.productKey}
                            placeholder={i.productName}
                            component={Input}
                            className="input input--sm input--withdrop"
                          />
                        </MobileFormFieldWrapper>
                      )
                    }
                  })
                }

              </Form>
            </BodyWrapper>
            <FooterWrapper className="m__foot--withtotal">

              {isTotal ? <>
                <span className="m__foot--withtotal__info">
                  <span className="m__body__info">Total:</span>
                  <span className="m__foot--withtotal__num">
                    <span className="m__foot--withtotal__sign">$</span>{currency(parseData.grandTotal)}</span>
                </span>
                {hasConvenienceFeeTextInSaleFeature && <div className="m__foot--note">Convenience Fee will be added later</div>}
              </> : <></>}

              <Button
                title={isTotal ? 'Next' : 'Select a Product to continue'}
                buttonStyle={isTotal ? buttonStyle.OUTL_LITE : buttonStyle.LARY}
                buttonSize={buttonSize.LG}
                buttonColor={isTotal ? buttonColor.PRIM : null}
                onClick={isTotal ? propsForm.submitForm : () => {
                }}
              />
            </FooterWrapper>
          </PageMobileWrapper>
        );
      }}
    />
  );
};

export default SaleStepProducts;
